import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { MenuRenderFn, MenuResolution, MenuTextMatch } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { LexicalTypeaheadMenuPlugin, MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { mergeRegister } from '@lexical/utils';
import type { SvgIconComponent } from '@mui/icons-material';
import { Box, ListItemIcon, ListItemText, MenuItem, Paper, Popper } from '@mui/material';
import type { SxProps, SystemStyleObject, Theme } from '@mui/system';
import type { VirtualElement } from '@popperjs/core';
import { BLUR_COMMAND, COMMAND_PRIORITY_LOW, FOCUS_COMMAND, KEY_DOWN_COMMAND } from 'lexical';
import type { LexicalEditor, TextNode } from 'lexical';
import type { ReactNode } from 'react';
import React, { Fragment, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18n';

const MENU_OFFSET = 3;
const SLASH_TRIGGER_REGEX = /(^|\s|\()\/([^\s/]*)$/;
const KEY_ARROW_DOWN = 'ArrowDown';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ENTER = 'Enter';
const KEY_ESCAPE = 'Escape';

const popperSx = { zIndex: 1300 };
const popperModifiers = [
    {
        name: 'offset',
        options: {
            offset: [0, MENU_OFFSET]
        }
    }
];

const menuPaperSx = {
    width: 260,
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: 1.25,
    p: 1,
    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.18)'
};

const menuItemSx = {
    alignItems: 'center',
    borderRadius: 1,
    gap: 1.25,
    minHeight: 54,
    px: 1.25,
    py: 0.75,
    whiteSpace: 'normal',
    '&:hover, &.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: 'action.hover'
    },
    '&.Mui-selected .MuiListItemText-primary': {
        color: 'text.primary'
    },
    '&.Mui-selected .MuiListItemText-secondary': {
        color: 'text.secondary'
    }
};

const iconWrapperBaseSx = {
    alignItems: 'center',
    borderRadius: 2,
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    width: 32
};

const primaryTextTypographyProps = {
    color: 'text.primary',
    fontSize: 12,
    fontWeight: 700
};

const secondaryTextTypographyProps = {
    color: 'text.secondary',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.2
};

const preventDefaultMouseDown = (event: MouseEvent) => event.preventDefault();

type SlashMacroMenuPosition = {
    top: number;
    left: number;
};

export type LexicalSlashMacroSelection = {
    editor: LexicalEditor;
    queryString: string;
    context?: unknown;
};

export type LexicalSlashMacroAction = {
    id: string;
    label: ReactNode;
    description?: ReactNode;
    Icon?: SvgIconComponent;
    iconContainerSx?: SystemStyleObject<Theme>;
    iconSx?: SxProps<Theme>;
    keywords?: string[];
    onSelect?: (selection?: LexicalSlashMacroSelection) => void;
};

class SlashMacroOption extends MenuOption {
    action: LexicalSlashMacroAction;

    constructor(action: LexicalSlashMacroAction) {
        super(action.id);
        this.action = action;
    }
}

type LexicalSlashMacroPluginProps = {
    id: string;
    actions: ReadonlyArray<LexicalSlashMacroAction>;
    disabled?: boolean;
    /** Opens the macro menu on editor focus, used by attachment-like rich text fields. */
    autoOpen?: boolean;
    /** Allows clicking an already-focused editor to re-open the auto-open menu. */
    enableAutoOpenOnRootClick?: boolean;
    /** Temporarily hides auto-open while another UI surface handles the selected macro action. */
    suppressAutoOpen?: boolean;
};

const getSearchableText = (action: LexicalSlashMacroAction): string => {
    const label = typeof action.label === 'string' ? action.label : '';
    const description = typeof action.description === 'string' ? action.description : '';
    return [action.id, label, description, ...(action.keywords || [])].join(' ').toLowerCase();
};

const checkForSlashTriggerMatch = (text: string): MenuTextMatch | null => {
    const match = SLASH_TRIGGER_REGEX.exec(text);
    if (!match) {
        return null;
    }

    const leadingText = match[1];
    const queryString = match[2];
    return {
        leadOffset: match.index + leadingText.length,
        matchingString: queryString,
        replaceableString: `/${queryString}`
    };
};

type SlashMacroMenuItemProps = {
    id: string;
    option: SlashMacroOption;
    selected: boolean;
    onSelect: (option: SlashMacroOption) => void;
    onHighlight: () => void;
};

function SlashMacroMenuItem({ id, option, selected, onSelect, onHighlight }: SlashMacroMenuItemProps) {
    const { action } = option;
    const Icon = action.Icon;

    return (
        <MenuItem
            id={id}
            role="menuitem"
            selected={selected}
            onMouseDown={preventDefaultMouseDown}
            onClick={() => onSelect(option)}
            onMouseEnter={onHighlight}
            sx={menuItemSx}
        >
            {Icon && (
                <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                        sx={{
                            ...iconWrapperBaseSx,
                            ...action.iconContainerSx
                        }}
                    >
                        <Icon sx={action.iconSx} />
                    </Box>
                </ListItemIcon>
            )}
            <ListItemText
                primary={action.label}
                secondary={action.description}
                primaryTypographyProps={primaryTextTypographyProps}
                secondaryTypographyProps={secondaryTextTypographyProps}
            />
        </MenuItem>
    );
}

type SlashMacroMenuProps = {
    id: string;
    position: SlashMacroMenuPosition;
    options: ReadonlyArray<SlashMacroOption>;
    selectedIndex: number | null;
    selectOptionAndCleanUp: (option: SlashMacroOption) => void;
    setHighlightedIndex: (index: number) => void;
};

function SlashMacroMenu({
    id,
    position,
    options,
    selectedIndex,
    selectOptionAndCleanUp,
    setHighlightedIndex
}: SlashMacroMenuProps) {
    const anchor = useMemo(() => getVirtualAnchor(position), [position]);
    const t = useI18n();
    const menuLabel = t('common.insert');

    return (
        <Popper
            id={`${id}-menu`}
            open={options.length > 0}
            anchorEl={anchor}
            placement="bottom-start"
            modifiers={popperModifiers}
            sx={popperSx}
        >
            <Paper role="menu" aria-label={menuLabel} sx={menuPaperSx} onMouseDown={preventDefaultMouseDown}>
                {options.map((option, index) => (
                    <SlashMacroMenuItem
                        key={option.action.id}
                        id={`${id}-menu-item-${option.action.id}`}
                        option={option}
                        selected={selectedIndex === index}
                        onSelect={selectOptionAndCleanUp}
                        onHighlight={() => setHighlightedIndex(index)}
                    />
                ))}
            </Paper>
        </Popper>
    );
}

const getMenuPositionFromRect = (rect: DOMRect): SlashMacroMenuPosition => {
    return {
        top: rect.bottom,
        left: rect.left
    };
};

const getEditorSelectionPosition = (editor: LexicalEditor): SlashMacroMenuPosition | null => {
    const rootElement = editor.getRootElement();
    if (!rootElement) {
        return null;
    }

    // Prefer the current selection so auto-open appears near the caret. If
    // focus has not settled yet, fall back to the editor root.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && rootElement.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        const selectionRect = range.getBoundingClientRect();
        const rect = selectionRect.width || selectionRect.height ? selectionRect : range.getClientRects()[0];
        if (rect) {
            return getMenuPositionFromRect(rect);
        }
    }

    return getMenuPositionFromRect(rootElement.getBoundingClientRect());
};

const getVirtualAnchor = (position: SlashMacroMenuPosition): VirtualElement => {
    return {
        getBoundingClientRect: () =>
            ({
                x: position.left,
                y: position.top,
                top: position.top,
                left: position.left,
                right: position.left,
                bottom: position.top,
                width: 0,
                height: 0
            }) as DOMRect
    };
};

export default function LexicalSlashMacroPlugin({
    id,
    actions,
    disabled = false,
    autoOpen = false,
    enableAutoOpenOnRootClick = false,
    suppressAutoOpen = false
}: LexicalSlashMacroPluginProps) {
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = useState<string | null>(null);
    const [menuPosition, setMenuPosition] = useState<SlashMacroMenuPosition | null>(null);
    const [autoOpenMenuPosition, setAutoOpenMenuPosition] = useState<SlashMacroMenuPosition | null>(null);
    const [autoOpenSelectedIndex, setAutoOpenSelectedIndex] = useState(0);
    const hasAutoOpenedRef = useRef(false);
    const hasScheduledInitialAutoOpenRef = useRef(false);
    const hasSelectedMacroActionRef = useRef(false);
    const autoOpenRootClickAnimationFrameRef = useRef<number | null>(null);
    const canAutoOpenRef = useRef(false);
    const hasActions = actions.length > 0;
    const isEnabled = !disabled && hasActions;
    const isAutoOpenEnabled = autoOpen && isEnabled;
    const shouldAutoOpen = isAutoOpenEnabled && !suppressAutoOpen;
    const isSlashTriggeredMenuOpen = menuPosition !== null;

    const options = useMemo(() => {
        const normalizedQuery = queryString?.toLowerCase() || '';
        return actions
            .filter((action) => !normalizedQuery || getSearchableText(action).includes(normalizedQuery))
            .map((action) => new SlashMacroOption(action));
    }, [actions, queryString]);

    const closeAutoOpenMenu = useCallback(() => {
        setAutoOpenMenuPosition(null);
    }, []);

    const cancelAutoOpenRootClickAnimationFrame = useCallback(() => {
        if (autoOpenRootClickAnimationFrameRef.current !== null) {
            window.cancelAnimationFrame(autoOpenRootClickAnimationFrameRef.current);
            autoOpenRootClickAnimationFrameRef.current = null;
        }
    }, []);

    const resetAutoOpenMenu = useCallback(() => {
        cancelAutoOpenRootClickAnimationFrame();
        hasAutoOpenedRef.current = false;
        hasScheduledInitialAutoOpenRef.current = false;
        hasSelectedMacroActionRef.current = false;
        closeAutoOpenMenu();
    }, [cancelAutoOpenRootClickAnimationFrame, closeAutoOpenMenu]);

    const onSelectOption = useCallback(
        (
            selectedOption: SlashMacroOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void,
            matchingString: string
        ) => {
            editor.update(() => {
                nodeToReplace?.remove();
            });
            closeMenu();
            selectedOption?.action?.onSelect?.({
                editor,
                queryString: matchingString
            });
            hasSelectedMacroActionRef.current = true;
            canAutoOpenRef.current = false;
        },
        [editor]
    );

    const onOpen = useCallback(
        (resolution: MenuResolution) => {
            closeAutoOpenMenu();
            setMenuPosition(getMenuPositionFromRect(resolution.getRect()));
        },
        [closeAutoOpenMenu]
    );

    const onClose = useCallback(() => {
        setMenuPosition(null);
    }, []);

    const handleQueryChange = useCallback(
        (nextQueryString: string | null) => {
            if (nextQueryString !== null) {
                closeAutoOpenMenu();
            }
            setQueryString(nextQueryString);
        },
        [closeAutoOpenMenu]
    );

    const selectAutoOpenOption = useCallback(
        (selectedOption: SlashMacroOption) => {
            closeAutoOpenMenu();
            selectedOption.action.onSelect?.({
                editor,
                queryString: ''
            });
            hasSelectedMacroActionRef.current = true;
            canAutoOpenRef.current = false;
        },
        [closeAutoOpenMenu, editor]
    );

    const handleAutoOpenKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!autoOpenMenuPosition) {
                return false;
            }

            let handled = true;

            switch (event.key) {
                case KEY_ARROW_DOWN:
                    setAutoOpenSelectedIndex((index) => Math.min(index + 1, options.length - 1));
                    break;
                case KEY_ARROW_UP:
                    setAutoOpenSelectedIndex((index) => Math.max(index - 1, 0));
                    break;
                case KEY_ENTER: {
                    const selectedOption = options[autoOpenSelectedIndex];
                    if (selectedOption) {
                        selectAutoOpenOption(selectedOption);
                    }
                    break;
                }
                case KEY_ESCAPE:
                    closeAutoOpenMenu();
                    break;
                default:
                    // Once the user types normally, the auto-open prompt should
                    // get out of the way and let Lexical handle the input.
                    closeAutoOpenMenu();
                    handled = false;
            }

            if (handled) {
                event.preventDefault();
            }
            return handled;
        },
        [autoOpenMenuPosition, autoOpenSelectedIndex, closeAutoOpenMenu, options, selectAutoOpenOption]
    );

    const canOpenAutoMenu = useCallback(
        () => shouldAutoOpen && !hasAutoOpenedRef.current && !hasSelectedMacroActionRef.current,
        [shouldAutoOpen]
    );
    canAutoOpenRef.current = canOpenAutoMenu();

    useEffect(() => {
        if (!canOpenAutoMenu()) {
            cancelAutoOpenRootClickAnimationFrame();
        }
    }, [canOpenAutoMenu, cancelAutoOpenRootClickAnimationFrame]);

    const openAutoMenu = useCallback(() => {
        // Once any macro action is selected, suppress auto-open until blur or
        // temporary external suppression resets it. Users can still type "/".
        if (!canOpenAutoMenu()) {
            return;
        }

        const position = getEditorSelectionPosition(editor);
        if (!position) {
            return;
        }

        hasAutoOpenedRef.current = true;
        hasScheduledInitialAutoOpenRef.current = true;
        canAutoOpenRef.current = false;
        setAutoOpenSelectedIndex(0);
        setAutoOpenMenuPosition(position);
    }, [canOpenAutoMenu, editor]);

    const handleAutoOpenRootClick = useCallback(() => {
        if (canOpenAutoMenu()) {
            canAutoOpenRef.current = true;
            cancelAutoOpenRootClickAnimationFrame();
            autoOpenRootClickAnimationFrameRef.current = window.requestAnimationFrame(() => {
                autoOpenRootClickAnimationFrameRef.current = null;
                if (canAutoOpenRef.current) {
                    openAutoMenu();
                }
            });
        }
    }, [canOpenAutoMenu, cancelAutoOpenRootClickAnimationFrame, openAutoMenu]);

    const renderMenu = useCallback<MenuRenderFn<SlashMacroOption>>(
        (anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
            const anchorElement = anchorElementRef.current;
            if (!anchorElement || options.length === 0) {
                return null;
            }
            // Lexical owns the slash-triggered menu lifecycle. We keep the
            // captured rect stable so filtering does not make the menu jump.
            const position = menuPosition || getMenuPositionFromRect(anchorElement.getBoundingClientRect());

            return (
                <SlashMacroMenu
                    id={id}
                    position={position}
                    options={options}
                    selectedIndex={selectedIndex}
                    selectOptionAndCleanUp={selectOptionAndCleanUp}
                    setHighlightedIndex={setHighlightedIndex}
                />
            );
        },
        [id, menuPosition, options]
    );

    useEffect(() => {
        if (hasScheduledInitialAutoOpenRef.current || !canOpenAutoMenu()) {
            return undefined;
        }

        // The grid editor may mount before the DOM selection is ready, so wait
        // one frame before reading the caret/root position.
        const animationFrame = window.requestAnimationFrame(openAutoMenu);

        return () => window.cancelAnimationFrame(animationFrame);
    }, [canOpenAutoMenu, openAutoMenu]);

    useEffect(() => {
        if (!shouldAutoOpen) {
            resetAutoOpenMenu();
        }
    }, [resetAutoOpenMenu, shouldAutoOpen]);

    useEffect(() => {
        if (!isAutoOpenEnabled) {
            return undefined;
        }

        return mergeRegister(
            editor.registerCommand(
                FOCUS_COMMAND,
                () => {
                    // Re-run on focus for cases where the first mount frame happened
                    // before Lexical moved focus into the contenteditable.
                    window.requestAnimationFrame(openAutoMenu);
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(KEY_DOWN_COMMAND, handleAutoOpenKeyDown, COMMAND_PRIORITY_LOW),
            editor.registerCommand(
                BLUR_COMMAND,
                () => {
                    resetAutoOpenMenu();
                    return false;
                },
                COMMAND_PRIORITY_LOW
            )
        );
    }, [editor, handleAutoOpenKeyDown, isAutoOpenEnabled, openAutoMenu, resetAutoOpenMenu]);

    useEffect(() => {
        if (!isAutoOpenEnabled || !enableAutoOpenOnRootClick) {
            return undefined;
        }

        let removeRootClickListener = () => {};
        const unregisterRootListener = editor.registerRootListener((rootElement) => {
            removeRootClickListener();
            removeRootClickListener = () => {};

            if (!rootElement) {
                return;
            }

            rootElement.addEventListener('click', handleAutoOpenRootClick);
            removeRootClickListener = () => rootElement.removeEventListener('click', handleAutoOpenRootClick);
        });

        return () => {
            removeRootClickListener();
            unregisterRootListener();
        };
    }, [editor, enableAutoOpenOnRootClick, handleAutoOpenRootClick, isAutoOpenEnabled]);

    useEffect(() => {
        return () => {
            cancelAutoOpenRootClickAnimationFrame();
        };
    }, [cancelAutoOpenRootClickAnimationFrame]);

    if (!isEnabled) {
        return null;
    }

    return (
        <Fragment>
            <LexicalTypeaheadMenuPlugin<SlashMacroOption>
                onQueryChange={handleQueryChange}
                onSelectOption={onSelectOption}
                onOpen={onOpen}
                onClose={onClose}
                triggerFn={checkForSlashTriggerMatch}
                options={options}
                menuRenderFn={renderMenu}
            />
            {autoOpenMenuPosition && !isSlashTriggeredMenuOpen && options.length > 0 && (
                <SlashMacroMenu
                    id={`${id}-auto-open`}
                    position={autoOpenMenuPosition}
                    options={options}
                    selectedIndex={autoOpenSelectedIndex}
                    selectOptionAndCleanUp={selectAutoOpenOption}
                    setHighlightedIndex={setAutoOpenSelectedIndex}
                />
            )}
        </Fragment>
    );
}
